import type { ReactNode } from "react";
import type { components } from "../../api/schema";
import { Pill } from "../../ui";

/** A switch changed outside Zenith, on the screens that can show it (#574,
 * ADR-0078 decision 3).
 *
 * A change made at a switch's console moves no generation and no held
 * document, so every other signal on these pages reads the switch as in
 * agreement. Two facts in the switch's own state report survive that: each
 * port's native VLAN, and the VLANs it holds an interface for.
 *
 * **Nothing here ever renders an agreement.** There is no tick, no green, no
 * "matches" and no count of zero, because the report carries no *tagged*
 * membership — a port this cannot fault may still differ in a way the report
 * does not carry. Where there is no disagreement these render **nothing at
 * all**, which is silence and not a reassurance. */

export type VlanDisagreement = components["schemas"]["SwitchVlanDisagreement"];
export type PortVlanDisagreement = components["schemas"]["SwitchPortVlanDisagreement"];

/** The disagreements keyed by the port they are about, for a table that draws
 * one row per port. */
export function vlansByPort(d?: VlanDisagreement): Map<string, PortVlanDisagreement> {
  const out = new Map<string, PortVlanDisagreement>();
  for (const p of d?.ports ?? []) out.set(p.port, p);
  return out;
}

/** How many things a reading has to say about a switch: ports that disagree
 * plus VLANs it holds that the cell does not.
 *
 * **Zero is never rendered by any caller**, which is why this returns a
 * number and not a label: a badge reading "0" on a switch's page is the
 * "absent capability as zero" `conventions/dashboards-as-code.md` forbids,
 * and here it would be worse than a wrong number — it would be an agreement,
 * which this signal may not state. */
export function vlanDisagreementCount(d?: VlanDisagreement): number {
  // An unmeasured reading has no count, and `0` would be the worst available
  // answer: it would say the reading was taken and found nothing.
  if (d?.not_measured) return 0;
  // A withheld reading has no count either, and for the same reason (#574
  // scope step 5). During convergence the findings are not absent because the
  // switch agrees; they are absent because Zenith cleared them.
  if (d?.converging) return 0;
  return (d?.ports?.length ?? 0) + (d?.unheld_vlans?.length ?? 0);
}

/** The marker in a port's VLAN cell. Absent unless that port disagrees.
 *
 * It names both values in its tooltip rather than in the cell, because the
 * cell is a column in a table of 28 ports and the sentence is the panel's
 * job; what the cell owes the operator is that there is something to open. */
// Restates ADR-0078 decision 3 — the state report's native VLAN may show a disagreement
// with the held document and may never show an agreement; the report cannot see a tagged
// membership, so the absence of this marker is not evidence the port matches.
export function PortVlanFlag({ d }: { d?: PortVlanDisagreement }): ReactNode {
  if (!d) return null;
  const called = d.reported_as ? ` (which the switch calls ${d.reported_as})` : "";
  return (
    <>
      {" "}
      <Pill
        tone="warn"
        title={`The switch reports this port${called} on VLAN ${d.reported}, where the configuration the controller holds puts it on ${d.held}. Applying a plan replaces what is on the switch. This is read from the switch's own report and cannot see a tagged membership, so it is not the whole picture.`}
      >
        switch reports {d.reported}
      </Pill>
    </>
  );
}

/** The port panel's sentence, which is where both values and the route back
 * are said in full. Absent unless the port disagrees. */
// Restates ADR-0078 decisions 3 and 5 — the disagreement is shown and never an agreement,
// and the route back is an edit of intent and an explicit apply, never this plan.
export function PortVlanNote({ d }: { d?: PortVlanDisagreement }): ReactNode {
  if (!d) return null;
  return (
    <p className="notice">
      The switch reports this port on VLAN <strong>{d.reported}</strong>
      {d.reported_as ? <> — it calls the port <code>{d.reported_as}</code> — </> : " "}
      where the configuration the controller holds puts it on <strong>{d.held}</strong>. Applying a plan sends the
      held configuration, and the switch takes it as its complete configuration, so this would be replaced
      along with any other change made on the switch by hand. If the switch is right and the planned
      settings are wrong, edit the planned settings to match, then plan and apply.
    </p>
  );
}

/** What a switch's page says about VLANs it holds that the cell's document
 * does not, and the count beside it. Absent when there is nothing to say.
 *
 * The VLAN half is listed rather than counted into the ports, because it is
 * not about a port: a VLAN created at a console and joined by no port still
 * reaches the report, which is what makes it a wider witness than the
 * per-port native VLANs. */
// Restates ADR-0078 decision 3 — one direction only, a VLAN the switch has that the cell
// does not; the reverse is a different claim and is not made anywhere.
// Restates ADR-0147 decision 1 — the window this reading is withheld in runs from Zenith's push
// to the moment Zenith can read a report carrying the new value, so the sentence names the push
// and never says the switch has settled.
export function SwitchVlanSummary({ d }: { d?: VlanDisagreement }): ReactNode {
  // **Spoken silence, not an empty space** (#574 scope step 6). A firmware no
  // run has read the state report on gets a sentence saying so, because
  // drawing nothing would read exactly like a switch in agreement — which is
  // the one thing this signal may never convey, and the reason the field is
  // present rather than absent in that case.
  if (d?.not_measured) {
    return (
      <p className="notice">
        What this switch reports about its VLANs is <strong>not read</strong>: no qualification run has
        measured the state report on{" "}
        {d.not_measured.firmware ? <code>{d.not_measured.firmware}</code> : "the firmware it runs, which the controller does not report"}.
        Nothing is claimed about it here, in either direction &mdash; a change made at this switch&rsquo;s
        console would not show, and its absence is not evidence that none was made.
      </p>
    );
  }
  // **The second spoken silence** (#574 scope step 5, ADR-0147). Zenith pushed
  // to this switch recently enough that its report cannot yet carry what was
  // sent, so the reading is withheld — and saying so is the whole point:
  // rendering nothing here is indistinguishable from a switch in agreement,
  // which is what `not_measured` above exists to avoid and what this branch
  // would otherwise reintroduce through the back door.
  //
  // It is checked after `not_measured` for the reason /v1 orders them that
  // way: an unmeasured firmware is the stronger silence, and stays true
  // whatever Zenith pushed a minute ago.
  if (d?.converging) {
    const pushed = new Date(d.converging.pushed_at);
    return (
      <p className="notice">
        What this switch reports about its VLANs is <strong>not read yet</strong>: it was sent a
        configuration at {pushed.toLocaleTimeString()}, and until the switch has reported back, a
        disagreement here would be that push still arriving rather than a change made at the
        console. Nothing is claimed about it in either direction until it settles.
      </p>
    );
  }
  const count = vlanDisagreementCount(d);
  if (!d || count === 0) return null;
  const unheld = d.unheld_vlans ?? [];
  const wrote = d.reported_at ? new Date(d.reported_at) : undefined;
  return (
    <p className="notice">
      <Pill tone="warn">
        {count} {count === 1 ? "disagreement" : "disagreements"}
      </Pill>{" "}
      between what this switch reports and what the controller holds for it.
      {unheld.length > 0 && (
        <>
          {" "}
          The switch has {unheld.length === 1 ? "an interface for VLAN" : "interfaces for VLANs"}{" "}
          <strong>{unheld.join(", ")}</strong>, which the controller&rsquo;s configuration does not.
        </>
      )}
      {wrote && <> Read from the report the switch wrote at {wrote.toLocaleTimeString()}.</>}
      {d.unreadable && <> Part of what the switch reports could not be compared, so this may not be all of it.</>}
    </p>
  );
}
